import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useNavigationType } from 'react-router-dom';

import { APP_URL_CHAT, STORAGE_KEY_SELF_CREATED_CHAT } from '../../../const';
import { useAdvisorLockGate } from '../../../hooks/useAdvisorLockGate';
import storageService from '../../../service/StorageService';
import {
    getActiveChatState,
    setUpdateRemoveChatModal,
    startChatWithAdvisor,
    startChatWithAdvisorByActivities,
    startChatWithDefaultAdvisor
} from '../../../store/slices/chatsSlice';
import Spinner from '../Spinner';
import styles from './styles.module.scss';

export const createChatBtnType = {
    advisorWidget: 'advisorWidget',
    advisorLanding: 'advisorLanding',
    default: 'default'
};

const CreateNewChat = ({ label = '', type = createChatBtnType.default, advisorAlias = 'veda', activityId = null, isActivity = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const { loading } = useSelector(getActiveChatState);
    const checkAdvisorLockBlocked = useAdvisorLockGate();

    const startChatByAlias = async () => {
        dispatch(setUpdateRemoveChatModal({ isOpen: false, alias: null }));
        return dispatch(startChatWithAdvisor(`alias/${advisorAlias}`)).then((result) => result);
    };

    const startChatByActivity = async () => {
        if (!activityId) {
            return;
        }
        return dispatch(startChatWithAdvisorByActivities(activityId)).then((result) => result);
    };

    const startChatById = async () => {
        // Routes through the user's `defaultAdvisorAlias` preference when set
        // (e.g. Ophelia). Falls back to the server-side default (Veda) when
        // the user has no preference, so v1 callers and Veda-default users
        // see identical behavior to pre-feature.
        return dispatch(startChatWithDefaultAdvisor()).then((result) => result);
    };

    const startChatFn = type !== createChatBtnType.default ? (isActivity ? startChatByActivity : startChatByAlias) : startChatById;

    const onChatClick = () => {
        if (loading) {
            return;
        }
        // HORO-10504: advisor-access gate (anonymous character-access AND
        // Zara's premium-subscriber gate — this used to check
        // `canStartChatWithAdvisor` directly, which only covers the
        // anonymous case, so a registered-but-not-premium user clicking
        // "Chat now" here for Zara bypassed her SUBSCRIBE gate entirely).
        // `type === default` (no explicit advisor pick — resolves the
        // user's own preference or the server default) is deliberately
        // excluded: there's no user-chosen alias to gate there.
        if (type !== createChatBtnType.default && !isActivity && advisorAlias && checkAdvisorLockBlocked(advisorAlias)) {
            return;
        }
        // Skip chat creation when we already know the destination advisor
        // and don't need a chat id afterwards (advisor-landing button,
        // non-activity). The advisor landing page creates the chat the
        // first time the user engages — tapping a home card or sending a
        // message — so a pre-emptive chat here just produces an orphan
        // chat for anyone who taps "Chat now" and then leaves.
        //
        // The other paths still need the existing flow:
        //   - default type (no `advisorAlias` known up-front; the API
        //     resolves the user's default advisor server-side).
        //   - activity chats (`isActivity`) bind to an activity id and
        //     need the chat-creation response to learn the advisor alias.
        //   - advisorWidget mode (auto-fires on REPLACE nav and may
        //     depend on the resulting chat id for the widget shell).
        if (type === createChatBtnType.advisorLanding && !isActivity && advisorAlias) {
            navigate(`/${APP_URL_CHAT}/${advisorAlias}`);
            return;
        }
        startChatFn()
            .then((result) => {
                if (result?.meta?.requestStatus === 'fulfilled') {
                    storageService.add(STORAGE_KEY_SELF_CREATED_CHAT, result?.payload?.id);
                    navigate(`/${APP_URL_CHAT}/${result?.payload?.advisor?.alias}/${result?.payload?.id}`);
                }
            })
            .catch((error) => {
                console.log(error);
                if (type === createChatBtnType.advisorWidget) {
                    navigate(`/${APP_URL_CHAT}/${advisorAlias}`);
                    return;
                }
            });
    };

    const handleBackToWidget = () => {
        window.removeEventListener('popstate', handleBackToWidget);
        window.history.go(-1);
    };

    useEffect(() => {
        if (type === createChatBtnType.advisorWidget && navigationType.toString() === 'REPLACE') {
            window.addEventListener('popstate', handleBackToWidget);
            onChatClick();
        }
    }, []);

    return type === createChatBtnType.advisorWidget ? (
        loading ? (
            <Spinner width={224} height={224} />
        ) : null
    ) : (
        <button className={classNames('home-Button', styles.btn)} onClick={onChatClick}>
            {loading ? (
                <div className={styles.spinner}>
                    <Spinner fill={'#000000'} width={30} height={30} />
                </div>
            ) : (
                <>{!!label ? label : t('labels.startChat')}</>
            )}
        </button>
    );
};

export default CreateNewChat;
